import { Navigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectOpportunityService } from '../../../services/project-opportunity';
import { ColumnBoxFlexFullWidth, FlexBox } from '../../common/boxes';
import { Loader } from '../../common/Loader';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ProjectOpportunityDetails from './ProjectOpportunityDetails';
import { useCallback, useMemo, useState } from 'react';
import { CLIENTS_PATH, PROJECT_OPPORTUNITIES_PATH } from '../../../router/paths';
import { ShadowContainer } from '../../common/ShadowContainer';
import { TEXTS } from '../../../core/texts';
import ClientInfo from '../ClientInfo';
import ProjectOpportunityEntityPositions, { IProjectPositionsProps } from './ProjectOpportunityEntityPositions';
import { IBaseProjectPosition, ICreateProjectPosition } from '../../../interfaces/projectPosition';
import { useGetProjectDetails } from '../../../queryHooks/project/useGetProjectOpportunityDetails';
import {
  GET_PROJECT_POSITIONS_QUERY_KEY,
  useGetProjectPositions
} from '../../../queryHooks/project/useGetProjectPositions';
import { KeyboardDoubleArrowLeft } from '@mui/icons-material';

const ProjectOpportunityEntity = () => {
  const [navigateToClientInfo, setNavigateToClientInfo] = useState(false);
  const { clientId: paramClientId, projectOpportunityId: paramprojectOpportunityId } = useParams();
  const [clientId, projectOpportunityId] = useMemo(() => [+(paramClientId || 0), paramprojectOpportunityId || ''], [paramClientId, paramprojectOpportunityId]);
  const queryClient = useQueryClient();
  const { data: project, isFetching: isLoadingProject } = useGetProjectDetails({ clientId, projectOpportunityId });
  const { data: positions, isFetching: isLoadingPositions } = useGetProjectPositions({ clientId, projectOpportunityId });
  const refreshPositions = useCallback(() => queryClient.invalidateQueries([GET_PROJECT_POSITIONS_QUERY_KEY]), [queryClient]);

  const isLoading = useMemo(() => isLoadingProject || isLoadingPositions, [isLoadingPositions, isLoadingProject]);
  const positionHandlers = useMemo<Omit<IProjectPositionsProps, 'positions' | 'project'>>(() => ({
    refreshPositions,
    handleAddNewPosition: (position: ICreateProjectPosition) => ProjectOpportunityService.addPositionToProject({
      clientId,
      projectOpportunityId,
      position
    }),
    handleRemovePosition: (positionId: string) => ProjectOpportunityService.deletePosition({ clientId, projectOpportunityId, positionId }),
    handleUpdatePosition: (positionId: string, position: IBaseProjectPosition) => ProjectOpportunityService.editPosition({
      clientId,
      projectOpportunityId,
      positionId,
      data: position
    }),
    handleGetApplicants: (positionId: string) => {
      return ProjectOpportunityService.getPositionApplicants({
        clientId,
        projectOpportunityId,
        positionId
      });
    },
    handleAddApplicant: (applicant) => ProjectOpportunityService.addApplicantFreelancer(applicant),
    handleAddNewSkillsToPosition: (positionId: string, skills: string[]) => ProjectOpportunityService.addSkillsToPosition({
      clientId,
      projectOpportunityId,
      positionId,
      skills
    }),
    handleRemoveSkillsFromPosition: (positionId: string, skills: string[]) => ProjectOpportunityService.deleteSkillsFromPosition({
      clientId,
      skills,
      projectOpportunityId,
      positionId
    }),
  }), [clientId, projectOpportunityId, refreshPositions]);

  const navigateToClientInfoHandler = useCallback(() => setNavigateToClientInfo(true), []);

  if (!projectOpportunityId || !clientId) {
    return (<Navigate to={PROJECT_OPPORTUNITIES_PATH}/>);
  }

  return (
    <ColumnBoxFlexFullWidth>
      {
        isLoading && <Loader/>
      }
      {
        project &&
        <ColumnBoxFlexFullWidth justifyContent={'center'}>
          <ShadowContainer mb={4}>
            <FlexBox alignItems={'center'} mb={2}>
              <Tooltip title={'Go To Client Profile'}>
                <IconButton sx={{ ml: -1 }} onClick={navigateToClientInfoHandler}>
                  <KeyboardDoubleArrowLeft color={'primary'}/>
                </IconButton>
              </Tooltip>
              <Typography>{TEXTS.CLIENT_INFO}</Typography>
            </FlexBox>
            <ClientInfo clientId={clientId}/>
          </ShadowContainer>
          <Box mb={4}>
            <ProjectOpportunityDetails clientId={clientId} project={project}/>
          </Box>
        </ColumnBoxFlexFullWidth>
      }
      {
        positions && project &&
        <ShadowContainer mb={4}>
          <ProjectOpportunityEntityPositions positions={positions} project={project} {...positionHandlers}/>
        </ShadowContainer>
      }
      {
        navigateToClientInfo && <Navigate to={CLIENTS_PATH + `/${project?.customerId}`}/>
      }
    </ColumnBoxFlexFullWidth>
  );
};

export default ProjectOpportunityEntity;